import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useInvoices from "../hooks/useInvoices";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { Invoice } from "../api/models/invoices.models";

const StyledTable = styled(DataTable)`
  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 1.5rem 1rem;
  }

  .p-datatable-thead > tr > th {
    border: none;
    font-weight: 500;
    font-size: 0.8rem;
  }
`;

const StyledLink = styled.a`
  color: var(--sumi-blue) !important;
  text-decoration: underline !important;
  cursor: pointer;
`;

interface InvoiceTableProps {
  invoices?: Invoice[];
  downloadInvoiceImageFn?: (invoiceId: number) => void;
  hideSorting?: boolean;
  showCompanyName?: boolean;
  showPagination?: boolean;
  rows?: number;
  setPage?: (page: number) => void;
  setRows?: (rows: number) => void;
  totalReports?: number;
  page?: number;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  invoices,
  downloadInvoiceImageFn,
  hideSorting = false,
  showCompanyName = false,
  showPagination = false,
  rows = 10,
  setPage,
  setRows,
  totalReports,
  page = 1,
}) => {
  const { t } = useTranslation();
  const { invoiceData, invoiceError, isLoadingInvoices, downloadInvoiceImage } =
    useInvoices();

  // Use passed invoices prop if available, otherwise use hook data
  const displayInvoices = invoices !== undefined ? invoices : invoiceData;
  const isLoading = invoices !== undefined ? false : isLoadingInvoices;
  const downloadFn =
    downloadInvoiceImageFn !== undefined
      ? downloadInvoiceImageFn
      : downloadInvoiceImage;

  const statusBody = (rowData: Invoice) => {
    return <span>{getStatusText(rowData.status)}</span>;
  };

  const deadlineDateBody = (rowData: Invoice) => {
    return <span>{moment(rowData.deadlineDateTime).format("DD.MM.YYYY")}</span>;
  };

  const createdDateBody = (rowData: Invoice) => {
    return <span>{moment(rowData.createdDateTime).format("DD.MM.YYYY")}</span>;
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0:
        return t("status-unpaid");
      case 1:
        return t("status-paid");
      case 2:
        return t("status-overdue");
      default:
        return status;
    }
  };

  const downloadInvoiceTemplate = (rowData: Invoice) => {
    return (
      <StyledLink
        href="#"
        className="download-invoice"
        onClick={() => downloadFn(rowData.id)}
      >
        {t("cmd-download-invoice")}
      </StyledLink>
    );
  };

  return (
    <LoadingContainer
      status={isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS}
    >
      <StyledTable
        value={displayInvoices || []}
        dataKey="id"
        tableStyle={{ minWidth: "60rem" }}
        showHeaders={true}
        emptyMessage={
          invoiceError ? t("error-loading-invoices") : t("text-no-invoices")
        }
        lazy={showPagination}
        paginator={showPagination}
        first={(page - 1) * rows}
        rows={rows}
        totalRecords={totalReports}
        onPage={(e) => {
          const newPage = Math.floor(e.first / e.rows) + 1;
          if (newPage !== page && setPage) {
            setPage(newPage);
          }
          if (e.rows !== rows && setRows) {
            setRows(e.rows);
          }
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={`{first} - {last} / ${totalReports}`}
      >
        <Column
          field="externalId"
          header={t("header-invoice-number")}
          sortable={!hideSorting}
        />

        {showCompanyName && (
          <Column
            field="companyName"
            header={t("label-company-name")}
            sortable={!hideSorting}
          />
        )}

        <Column
          field="createdDateTime"
          header={t("header-created")}
          body={createdDateBody}
          sortable={!hideSorting}
        />

        <Column
          field="deadlineDateTime"
          header={t("header-deadline")}
          body={deadlineDateBody}
          sortable={!hideSorting}
        />

        <Column
          field="totalMount"
          header={t("header-amount")}
          sortable={!hideSorting}
        />
        {/* <Column
          field="status"
          header={t("header-status")}
          body={statusBody}
          sortable={!hideSorting}
          style={{ width: "120px" }}
        /> */}
        <Column body={downloadInvoiceTemplate} style={{ width: "150px" }} />
      </StyledTable>
    </LoadingContainer>
  );
};

export default InvoiceTable;
